/**
 * Pure routing predicates for the sign-up step machine.
 *
 * Rules for this module:
 * - No React, no context, no imports from SignUpProvider. Plain arguments in,
 *   booleans out, so every predicate is unit-testable without a render.
 * - Behaviour-preserving. Each predicate reproduces one expression that lives
 *   in SignUpProvider.js today, named in its doc comment. Where two
 *   expressions look alike but differ, they stay two predicates -- see
 *   isBootstrapTrialPlan.
 *
 * Two exceptions to behaviour-preservation, each flagged in its own doc
 * comment: `requiresProfileStep`, which reproduces no existing expression, and
 * `readIsLinkedToGoogle`, which deliberately CORRECTS one -- the provider read
 * the flag off the wrong level of the validate response.
 */

/** saveStepAuthenticate -- `userData.googleId` */
export const isGoogleUser = (user) => !!user?.googleId

/** saveStepAuthenticate, saveStepSelectPlan -- `userData.neatUserId` / `user.neatUserId` */
export const hasNeatUserId = (user) => !!user?.neatUserId

/**
 * saveStepAuthenticate, email branch -- `validation.user?.neatUserId`.
 *
 * Deliberately separate from hasNeatUserId. The Google branch tests
 * `userData.neatUserId` (assigned from the validate response just before)
 * while the email branch tests `validation.user?.neatUserId` directly. The two
 * should agree, but they are not the same expression, and collapsing them
 * would quietly assume they always do.
 */
export const validationHasNeatUserId = (validation) => !!validation?.user?.neatUserId

/** saveStepAuthenticate, saveStepResetPassword -- `validation.status === 1` */
export const isPurchasable = (validation) => validation?.status === 1

/**
 * The linked-to-Google flag as squidward returns it.
 *
 * It sits at the TOP LEVEL of the validate response, not under `user`:
 * squidward's orderValidationHandler initialises it on the response root and
 * assigns it there, and the `user` object it builds carries only email,
 * neatUserId, firstName and lastName.
 *
 * Both readers of the flag go through here so the response shape is stated in
 * exactly one place. The provider previously read it off `user`, a key that is
 * never present, so its stored copy was always false while the routing arm
 * below read the real value -- two readers of one flag that disagreed.
 *
 * Strict `=== true`: only the boolean counts, a truthy string does not.
 */
export const readIsLinkedToGoogle = (validation) => validation?.isLinkedToGoogle === true

/** saveStepAuthenticate -- `validation.status === 1 && validation.isLinkedToGoogle === true` */
export const isLinkedAndPurchasable = (validation) => (
  isPurchasable(validation) && readIsLinkedToGoogle(validation)
)

/** handleRedirectToApp -- `if (jwt)`; jwt is the /t query param, '' when absent */
export const hasJwt = (jwt) => !!jwt

/** handleRedirectToApp -- `else if (order?.ssoUrl)` */
export const orderHasSsoUrl = (order) => !!order?.ssoUrl

/**
 * The bootstrap trial predicate from the plan-fetch useEffect's
 * `exists && oauthToken` branch.
 *
 * This is NOT `shouldSkipPlanSelect`, despite looking like it:
 *
 *   bootstrap:            plan.isTrial || plan.sku.startsWith('vault')  || plan.sku?.startsWith('neathome')
 *   shouldSkipPlanSelect: plan.isTrial || plan.sku === 'vault50'        || plan.sku?.startsWith('neathome')
 *
 * Prefix match vs exact match: a `vault10` plan satisfies the bootstrap
 * predicate but not shouldSkipPlanSelect. Normalising the two would be a
 * behaviour change, so they stay distinct.
 *
 * A plan that never loaded is not a trial plan.
 */
export const isBootstrapTrialPlan = (plan) => !!(
  plan?.isTrial || plan?.sku?.startsWith('vault') || plan?.sku?.startsWith('neathome')
)

/**
 * The only predicate in this module that is NOT behaviour-preserving.
 *
 * The profile step (`newUser`) collects firstName, lastName and a NEW password
 * -- the data needed to CREATE an account. A user who already has one does not
 * need it. There are two ways to already have one:
 *
 *   - a Google user (`googleId`). The identity supplies the name and Google
 *     holds the credential. `SignUp/NewUser.js` already encodes half of
 *     this: `password: user.googleId ? Yup.string() : passwordRules()`.
 *   - a user squidward has already identified (`neatUserId`). Reached by
 *     returning from AUTH_URL with an `oauthToken`, which squidward resolves
 *     via StayPuft (`orderValidationHandler.js`) and answers with a
 *     populated `neatUserId`, `firstName` and `lastName`.
 *
 * Keyed on the account rather than on Googleness, because Googleness is one
 * instance of the general fact and the narrower predicate cannot express the
 * second arm. The second arm only became reachable once the bootstrap
 * transition started passing the validate response as its `user` override
 * -- before that it read a stale render closure and saw `{}`.
 */
export const requiresProfileStep = (user) => !isGoogleUser(user) && !hasNeatUserId(user)

/**
 * Context-shaped adapters, used by transitions.js.
 *
 * The predicates above take plain arguments so they can be tested in isolation,
 * but the table needs every guard to share one call signature. These adapt the
 * primitives to a single `context` object; they add no logic of their own,
 * except `skipsBootstrapPlanSelect`, which joins the site's plan-select switch
 * to the bootstrap trial predicate.
 *
 * `shouldShowUpsell` and `shouldSkipPlanSelect` are computed by the provider
 * from React state and arrive already evaluated, so they are read straight
 * off the context rather than recomputed here.
 */
export const contextGuards = {
  always: () => true,
  isGoogleUser: (ctx) => isGoogleUser(ctx.user),
  hasNeatUserId: (ctx) => hasNeatUserId(ctx.user),
  validationHasNeatUserId: (ctx) => validationHasNeatUserId(ctx.validation),
  isLinkedAndPurchasable: (ctx) => isLinkedAndPurchasable(ctx.validation),
  isPurchasable: (ctx) => isPurchasable(ctx.validation),
  isBootstrapTrialPlan: (ctx) => isBootstrapTrialPlan(ctx.plan),
  // A site that sells no Neat plans turns plan select off; the OAuth return
  // then routes every plan the way it routes a trial plan.
  skipsBootstrapPlanSelect: (ctx) => !!ctx.planSelectDisabled || isBootstrapTrialPlan(ctx.plan),
  hasJwt: (ctx) => hasJwt(ctx.jwt),
  orderHasSsoUrl: (ctx) => orderHasSsoUrl(ctx.order),
  shouldShowUpsell: (ctx) => !!ctx.shouldShowUpsell,
  shouldSkipPlanSelect: (ctx) => !!ctx.shouldSkipPlanSelect,
  requiresProfileStep: (ctx) => requiresProfileStep(ctx.user),
  // Named rather than negated inline: the table has no negation syntax, and a
  // bypass row saying what it does reads better than one saying what it is not.
  skipsProfileStep: (ctx) => !requiresProfileStep(ctx.user),
}
